import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { Building2, Scale } from 'lucide-react-native';
import { type ReactNode, useState } from 'react';
import { ScrollView } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { type UnderlineTab, UnderlineTabs } from '@/components/UnderlineTabs';
import { UserProfileSidebar } from '@/components/UserProfileSidebar';
import { BecomeMentorCard } from '@/components/fellows-corner/BecomeMentorCard';
import { FellowsSeedQuestions } from '@/components/fellows-corner/FellowsSeedQuestions';
import {
  MentorSelectCard,
  MentorSelectCardSkeleton,
} from '@/components/fellows-corner/MentorSelectCard';
import { MentorshipGuidelines } from '@/components/fellows-corner/MentorshipGuidelines';
import { MentorshipInquiryForm } from '@/components/fellows-corner/MentorshipInquiryForm';
import { Header } from '@/components/header';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  FELLOWS_MENTOR_PARAM,
  getFellowMentor,
  resolveMentorTargetFromExpert,
} from '@/constants/fellowsCorner';
import { ROUTES } from '@/constants/routes';
import { useFellowSeedQA } from '@/hooks/useFellowSeedQA';
import { getMentorsV2QueryOptions } from '@/services/getMentorsV2';
import { segmentClient } from '@/util/analytics';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameOrFallback } from '@/util/getProfileNameAndInitials';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';

type MentorDetailTab = 'profile' | 'questions';

/**
 * Underline tab bar for the mentor detail column, toggling between the mentor's
 * profile and their seed Q&A. Rendered in the profile sidebar's `afterHeader`
 * slot while the page owns the active tab and swaps the sidebar body via
 * `hideSections`. Local two-tab control since there's no shared tabs primitive.
 */
function MentorDetailTabs({
  activeTab,
  onChange,
  questionCount,
}: {
  activeTab: MentorDetailTab;
  onChange: (tab: MentorDetailTab) => void;
  questionCount: number;
}) {
  const tabs: UnderlineTab<MentorDetailTab>[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'questions', label: 'Questions', count: questionCount },
  ];

  return <UnderlineTabs tabs={tabs} activeKey={activeTab} onChange={onChange} className="px-5" />;
}

export function FellowsCornerPage() {
  /**
   * The mentors endpoint carries every field the lineup renders, so the list
   * needs this one request and nothing else. It used to fetch each mentor's
   * public profile as well, which cost a second round trip and held the whole
   * lineup behind whichever profile was slowest. Order is meaningful, so the
   * list is rendered as returned.
   *
   * The selected mentor's full profile still loads, in the detail column, but
   * lazily and for one mentor rather than upfront for all of them.
   */
  const mentorDirectoryQuery = useQuery(getMentorsV2QueryOptions({ limit: 25 }));

  const mentorUsers = mentorDirectoryQuery.data?.mentors ?? [];

  const isLoadingMentors = mentorDirectoryQuery.isPending;

  /**
   * The database determines who appears. The optional registry only adds
   * Fellows-specific presentation and routing overrides for known mentors.
   */
  const mentors: ExpertProfile[] = mentorUsers.map((mentorUser) => {
    const mentorId = String(mentorUser.id);

    const registryMentor = getFellowMentor(mentorId);

    return {
      id: mentorId,
      name: getProfileNameOrFallback(mentorUser, registryMentor?.name ?? ''),
      title: mentorUser.position ?? registryMentor?.title,
      speciality: mentorUser.specialty ?? undefined,
      institution: mentorUser.institution ?? undefined,
      avatarSrc: getAvatarUrl(mentorUser.avatar) || undefined,
      description: mentorUser.bio ?? registryMentor?.blurb,
    };
  });

  // A deep link (e.g. the global banner) can preselect a mentor via the
  // `mentor` query param; ignored unless it matches a known registry mentor.
  const params = useLocalSearchParams<{ [FELLOWS_MENTOR_PARAM]?: string }>();

  const paramMentorId =
    typeof params[FELLOWS_MENTOR_PARAM] === 'string' &&
    mentors.some((mentor) => mentor.id === params[FELLOWS_MENTOR_PARAM])
      ? params[FELLOWS_MENTOR_PARAM]
      : null;

  // `selectedId` is the user's explicit pick. It falls back to a deep-linked
  // mentor, then the first mentor, so the ask form and detail are shown by
  // default rather than gated behind a pick.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A question the fellow chose to reuse from the seed Q&A, prefilled into the
  // ask form. Cleared whenever a different mentor is picked so the prefill can't
  // carry across mentors (whose seed questions differ).
  const [prefillMessage, setPrefillMessage] = useState<string | null>(null);

  // Which body the profile sidebar shows: the mentor's profile or their seed
  // Q&A. Kept at the page level (not per-mentor) so switching mentors preserves
  // the chosen tab for easy comparison.
  const [detailTab, setDetailTab] = useState<MentorDetailTab>('profile');

  const selectedMentorId = selectedId ?? paramMentorId ?? mentors[0]?.id ?? null;

  const selectedMentor = mentors.find((mentor) => mentor.id === selectedMentorId) ?? null;

  // Attorney mentors get legal profile icons instead of the sidebar's clinical
  // defaults, keyed off the registry `profession`.
  const isAttorneyMentor =
    selectedMentor != null && getFellowMentor(selectedMentor.id)?.profession === 'attorney';

  const seedQuestions = useFellowSeedQA(selectedMentor?.id ?? '');

  /**
   * A mentor with no seed questions shows no Questions tab, so the selection is
   * derived rather than stored. Switching from a mentor who has questions to one
   * who does not would otherwise leave the stored tab on `questions`, which hides
   * the profile sections and renders an empty list, blanking the whole sidebar.
   */
  const effectiveDetailTab: MentorDetailTab = seedQuestions.length > 0 ? detailTab : 'profile';

  const handleSelectMentor = (mentorId: string) => {
    segmentClient
      .track(ANALYTICS_EVENTS.FELLOWS_MENTOR_SELECTED, { mentor_id: mentorId })
      ?.catch(() => {});

    setSelectedId(mentorId);

    setPrefillMessage(null);
  };

  // Away state rides along on the listing ('2' = the manual Active/Away
  // toggle). Consumed only by the ask form, which names the mentor it applies
  // to, so several mentors being away needs no aggregate treatment.
  const awayMentorIds = new Set(
    mentorUsers.filter((mentor) => mentor.status === '2').map((mentor) => String(mentor.id))
  );

  const [showGuidelines, setShowGuidelines] = useState(false);

  const handleOpenGuidelines = () => {
    segmentClient.track(ANALYTICS_EVENTS.FELLOWS_GUIDELINES_VIEWED)?.catch(() => {});

    setShowGuidelines(true);
  };

  const handleRetryMentors = () => {
    mentorDirectoryQuery.refetch().catch(() => {});
  };

  let mentorListContent: ReactNode;

  if (isLoadingMentors) {
    mentorListContent = <MentorSelectCardSkeleton />;
  } else if (mentorDirectoryQuery.isError) {
    mentorListContent = (
      <VStack className="items-start py-6" space="sm">
        <Text size="sm" className="text-typography-600">
          We couldn&apos;t load mentors right now.
        </Text>
        <Button size="sm" variant="outline" onPress={handleRetryMentors}>
          <ButtonText>Try again</ButtonText>
        </Button>
      </VStack>
    );
  } else if (mentors.length === 0) {
    mentorListContent = (
      <Text size="sm" className="py-6 text-typography-600">
        No mentors are available right now.
      </Text>
    );
  } else {
    mentorListContent = (
      <VStack space="sm">
        {mentors.map((mentor) => (
          <MentorSelectCard
            key={mentor.id}
            expert={mentor}
            isSelected={selectedMentorId === mentor.id}
            onSelect={() => handleSelectMentor(mentor.id)}
          />
        ))}
      </VStack>
    );
  }

  return (
    <ScrollView
      className="flex-1 bg-background-0"
      contentInsetAdjustmentBehavior="automatic"
      showsVerticalScrollIndicator={false}>
      <Header
        headerShown={!isWeb}
        contentScrollsUnderHeader
        isBackButton
        backRoute={ROUTES.DIRECTORY}
        title="Fellows Corner"
      />

      {/* Web: a full-bleed band matching the directory hero, the same bg-primary-500 field with
          white type so both Primum directory surfaces read as one. Native names the screen in
          its bar and keeps only the description, as plain text: a colored band would slide
          under the floating bar and leave its dark title on dark green. */}
      <View className={twJoin('w-full', isWeb && 'bg-primary-500')}>
        <View className="mx-auto w-full max-w-screen-xl px-5 py-2.5 md:px-10">
          <View className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <VStack className="min-w-0 md:flex-1">
              {isWeb ? (
                <Text size="2xl" className="text-typography-white">
                  Fellows Corner
                </Text>
              ) : null}

              <Text
                className={twJoin(
                  'text-base leading-relaxed',
                  isWeb ? 'text-typography-white' : 'text-typography-700'
                )}>
                Ask a mentor about what they don&apos;t teach you in oncology fellowship.
              </Text>
            </VStack>
          </View>
        </View>
      </View>

      <VStack className="mx-auto w-full max-w-screen-xl px-5 pb-10 pt-6 md:px-10" space="lg">
        <View className="flex flex-col gap-6 md:flex-row md:items-start md:gap-x-10">
          {/* Left column: the mentor lineup and, once one is picked, the ask
              form. Keeping selection here lets the detail on the right own the
              full column height. */}
          <VStack className="w-full md:flex-1" space="lg">
            <VStack space="sm">
              <HStack className="flex-wrap items-center justify-between gap-3">
                <Heading size="sm" className="text-typography-900">
                  Meet your mentors
                </Heading>
                {isWeb ? <BecomeMentorCard /> : null}
              </HStack>
              {mentorListContent}
            </VStack>

            {selectedMentor ? (
              <MentorshipInquiryForm
                key={selectedMentor.id}
                mentor={resolveMentorTargetFromExpert({
                  id: selectedMentor.id,
                  name: selectedMentor.name,
                })}
                isAway={selectedMentor ? awayMentorIds.has(selectedMentor.id) : false}
                initialMessage={prefillMessage ?? undefined}
                onOpenGuidelines={handleOpenGuidelines}
              />
            ) : null}
          </VStack>

          {/* Right column: the selected mentor's real expert profile at full
              column height, or the how-it-works panel before a pick. */}
          <View className="hidden w-full md:flex md:flex-1">
            {selectedMentor ? (
              <UserProfileSidebar
                key={selectedMentor.id}
                expert={selectedMentor}
                expertId={selectedMentor.id}
                hideSections={effectiveDetailTab === 'questions'}
                roleIcon={isAttorneyMentor ? Scale : undefined}
                institutionIcon={isAttorneyMentor ? Building2 : undefined}
                afterHeader={
                  seedQuestions.length > 0 ? (
                    <VStack space="lg">
                      <MentorDetailTabs
                        activeTab={effectiveDetailTab}
                        onChange={(tab) => {
                          segmentClient
                            .track(ANALYTICS_EVENTS.FELLOWS_DETAIL_TAB_SELECTED, {
                              mentor_id: selectedMentor.id,
                              tab,
                            })
                            ?.catch(() => {});

                          setDetailTab(tab);
                        }}
                        questionCount={seedQuestions.length}
                      />
                      {effectiveDetailTab === 'questions' ? (
                        <FellowsSeedQuestions
                          mentorId={selectedMentor.id}
                          mentorName={selectedMentor.name}
                          mentorAvatarSrc={selectedMentor.avatarSrc}
                          items={seedQuestions}
                          onAskQuestion={(question) => {
                            segmentClient
                              .track(ANALYTICS_EVENTS.FELLOWS_QUESTION_SELECTED, {
                                mentor_id: selectedMentor.id,
                              })
                              ?.catch(() => {});

                            setPrefillMessage(question);
                          }}
                        />
                      ) : null}
                    </VStack>
                  ) : null
                }
              />
            ) : null}
          </View>
        </View>
      </VStack>

      <MentorshipGuidelines isOpen={showGuidelines} onClose={() => setShowGuidelines(false)} />
    </ScrollView>
  );
}
